import { FC, useEffect, useState } from 'react';
import { FlightReportErrorMessages, IFlightReport } from '../../../../model/IFlightReport';
import { GoADate, GoAFormItem, GoAInputDate, GoAInputText, GoARadioGroup, GoARadioItem } from '@abgov/react-components';
import moment from 'moment';
import { useAuth } from 'react-oidc-context';
import { useSetRecoilState, useRecoilValue } from 'recoil';
import { FlightReportCRUD } from '../../../../operations/FlightReportCRUD';
import { hiringContractsState, activeContractsState } from '../../../../state/DomainService.state';
import { IArrayOption } from '../../../../model/IArrayOption';
import { getContractRegistration, getFlyingRegistration } from '../../../../services/HiringContractService';
import { SingleValue } from 'react-select';
import Select from 'react-select';
import styles from './Summary.module.scss';
import { validateDecimal } from '../../../../utils/checkInput';
import { bindMutltiSelectDropdown, getCorporateRegion } from '../../../../services/domainServices/CorporateRegionDomainService';

const { dataContainer } = styles;
interface ISummaryProps {
  flightReport: IFlightReport;
  isEdit?: boolean;
  ruleCode: string;
  onChange: (updatedFields: Partial<IFlightReport>) => void;
  onChangeUnservicableReport: (value: boolean) => void;
  onNoFlyDay: (value: boolean) => void;
  // onNotUnservicableReport: () => void;
  corporateRegionPaginationResult: any;
}
const Summary: FC<ISummaryProps> = ({
  flightReport,
  isEdit,
  ruleCode,
  onChange,
  onNoFlyDay,
  // onNotUnservicableReport,
  corporateRegionPaginationResult,
  onChangeUnservicableReport,
}) => {
  const auth = useAuth();
  //Contract registration
  const setHiringContracts = useSetRecoilState(hiringContractsState);
  const hiringContracts = useRecoilValue(hiringContractsState);

  //Active registration
  const setActiveContracts = useSetRecoilState(activeContractsState);
  const activeContracts = useRecoilValue(activeContractsState);

  const [contractRegistrationOptions, setContractRegistrationOptions] = useState<IArrayOption[]>([]);
  const [flyingRegistrationOptions, setFlyingRegistrationOptions] = useState<IArrayOption[]>([]);
  const [forestAreaOptions, setForestAreaOptions] = useState<IArrayOption[]>([]);
  useEffect(() => {
    if (corporateRegionPaginationResult !== null) {
      const forestAreaResult = bindMutltiSelectDropdown(corporateRegionPaginationResult);
      setForestAreaOptions(forestAreaResult);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hiringContracts !== undefined) {
      const options =
        hiringContracts?.map((contract) => ({
          label: contract?.aircraftRegistration,
          value: contract?.id,
        })) ?? [];

      if (options) {
        setContractRegistrationOptions(options);
      }
    }

    if (activeContracts !== undefined) {
      const options =
        activeContracts?.map((contract) => ({
          label: contract?.aircraftRegistration,
          value: contract?.id,
        })) ?? [];

      if (options.find((t) => t.value === flightReport.flyingRegistrationId) === undefined && options && flightReport) {
        let obj: IArrayOption = {
          label: flightReport.flyingRegistrationName,
          value: flightReport.flyingRegistrationId,
        };
        options.push(obj);
      }

      if (options) {
        setFlyingRegistrationOptions(options);
      }
    }
  }, [flightReport, hiringContracts, activeContracts]);

  async function getActiveContracts(reportDate: string, searchValue: string = '') {
    try {
      const activeContractsResponse = await FlightReportCRUD.getActiveContract(reportDate, searchValue, auth.user?.access_token as string);

      const options =
        activeContractsResponse?.map((contract) => ({
          label: contract?.aircraftRegistration,
          value: contract?.id,
        })) ?? [];
      if (options) {
        setFlyingRegistrationOptions(options);
      }
    } catch (Error) {
      console.log('Error', Error);
    }
  }

  async function handleDateChange(selectedDate: GoADate) {
    if (selectedDate) {
      const ftDate = moment(selectedDate).format('yyyy-MM-DD');
      if (ftDate > moment().format('yyyy-MM-DD')) {
        onChange({
          flightReportDateError: FlightReportErrorMessages.FlightReportDateErrorMessage,
        });
        return;
      }
      let splitDate = ftDate.split('-');

      if (parseInt(splitDate[0]) >= 1950 && parseInt(splitDate[2]) <= 31) {
        //Get contract registartion & flying registration based on flight report date
        var hiringContracts = await FlightReportCRUD.getHiringContract(ftDate, auth.user?.access_token as string);
        var activeContracts = await FlightReportCRUD.getActiveContract(ftDate, '', auth.user?.access_token as string);
        setHiringContracts(hiringContracts);
        setActiveContracts(activeContracts);

        onChange({ flightReportDateError: '', flightReportDate: ftDate });
      }
    } else {
      onChange({ flightReportDateError: '', flightReportDate: '' });
    }
  }

  return (
    <div className={dataContainer}>
      <div>
        <GoAFormItem error={flightReport.flightReportDateError} label='Flight Date' requirement={isEdit ? 'required' : undefined}>
          {isEdit && (
            <GoAInputDate
              name='dtFlightDate'
              placeholder='YYYY-MM-DD'
              value={flightReport?.flightReportDate != null && flightReport?.flightReportDate !== '' ? flightReport?.flightReportDate : ''}
              min={new Date(1950, 1, 1)}
              max={new Date()}
              width='100%'
              disabled={flightReport?.flightReportId && flightReport?.flightReportDate ? true : false}
              onChange={(name, value) => {
                handleDateChange(value);
              }}
            />
          )}
          {!isEdit && <label>{flightReport?.flightReportDate}</label>}
        </GoAFormItem>
      </div>
      <div>
        <GoAFormItem label='AO-02 Number'>
          {isEdit && (
            <GoAInputText
              name='AO02Number'
              value={flightReport?.ao02Number as string}
              width='100%'
              maxLength={10}
              onChange={(name, value) => {
                onChange({
                  ao02Number: value.slice(0, 10),
                });
              }}
            />
          )}
          {!isEdit && <label>{flightReport?.ao02Number}</label>}
        </GoAFormItem>
      </div>
      <div>
        <GoAFormItem error={flightReport.contractRegistrationError} label='Contract Registration' requirement={isEdit ? 'required' : undefined}>
          {isEdit && (
            <Select
              name='selContractRegistration'
              options={contractRegistrationOptions}
              placeholder='--Select--'
              isDisabled={!flightReport.flightReportDate || flightReport?.flightReportId}
              value={contractRegistrationOptions.find((t) => t.value === flightReport.contractRegistration?.id) ?? null}
              onChange={async (value: SingleValue<IArrayOption>) => {
                if (value) {
                  var contractRegistration = getContractRegistration(hiringContracts, value.value);

                  var objVendor = {
                    name: contractRegistration?.name1,
                    financeVendorId: contractRegistration?.financeVendorId,
                  };

                  var CharterNoteListResult;
                  if (contractRegistration?.hiringDetailsId) {
                    CharterNoteListResult = await FlightReportCRUD.getCharterNotes(
                      contractRegistration?.hiringDetailsId,
                      auth.user?.access_token as string,
                    );
                  }

                  onChange({
                    contractRegistration: contractRegistration ?? undefined,
                    contractRegistrationError: contractRegistration ? '' : FlightReportErrorMessages.ContractRegistrationErrorMessage,
                    contractRegistrationId: value.value,
                    hiringStartDate: contractRegistration?.effectiveDate,
                    hiringEndDate: contractRegistration?.terminationDate,
                    pointOfHire: contractRegistration?.pointOfHire,
                    remark: contractRegistration?.remark,
                    aircraftWingType: contractRegistration?.wingType,
                    aircraftSize: contractRegistration?.aircraftSize,
                    aircraftType: contractRegistration?.aircraftType,

                    financeVendorId: contractRegistration?.financeVendorId,
                    vendorName: contractRegistration?.name1,

                    vendor: objVendor,
                    flyingRegistration: contractRegistration ?? undefined,
                    flyingRegistrationName: contractRegistration?.aircraftRegistration,
                    flyingRegistrationId: value.value,
                    hiringDetailsId: contractRegistration?.hiringDetailsId,
                    charternotelist: CharterNoteListResult,
                  });
                }
              }}
              onInputChange={(value: any) => {
                if (value) contractRegistrationOptions.find((t) => t.value === value);
              }}
              isSearchable={true}
            />
          )}
          {!isEdit && <label>{flightReport?.contractRegistration?.aircraftRegistration}</label>}
        </GoAFormItem>
      </div>

      <div>
        <GoAFormItem error={flightReport.flyingRegistrationError} label='Flying Registration' requirement={isEdit ? 'required' : undefined}>
          {isEdit && (
            <Select
              name='selFlyingRegistration'
              options={flyingRegistrationOptions}
              placeholder='--Select--'
              isDisabled={!flightReport.flightReportDate}
              value={
                flightReport.flyingRegistrationId === null
                  ? null
                  : flyingRegistrationOptions.find((t) => t.value === flightReport.flyingRegistrationId)
              }
              onChange={(value: any) => {
                if (value) {
                  var flyingRegistration = getFlyingRegistration(activeContracts, value.value);

                  onChange({
                    flyingRegistration: flyingRegistration ?? undefined,
                    flyingRegistrationId: value.value,
                    flyingRegistrationName: value.label,
                    flyingRegistrationError: flyingRegistration ? '' : FlightReportErrorMessages.FlyingRegistrationErrorMessage,
                  });
                }
              }}
              onInputChange={(value: any) => {
                if (value) getActiveContracts(flightReport.flightReportDate, value);
              }}
              isSearchable={true}
            />
          )}
          {!isEdit && <label>{flightReport?.flyingRegistrationName}</label>}
        </GoAFormItem>
      </div>
      <div>
        <GoAFormItem label='Meter Unserviceable?'>
          <div className='left'>
            {isEdit && (
              <GoARadioGroup
                name='rbtnMeterUnserviceable'
                value={flightReport?.meterUS === true ? 'Yes' : 'No'}
                orientation='horizontal'
                onChange={(_name, value) => {
                  let result = value === 'Yes' ? true : false;
                  onChange({ meterUS: result });
                }}
              >
                <GoARadioItem value='Yes' name={'Yes'} />
                <GoARadioItem value='No' name={'No'} />
              </GoARadioGroup>
            )}
            {!isEdit && <label>{flightReport?.meterUS === true ? 'Yes' : 'No'}</label>}
          </div>
        </GoAFormItem>
      </div>
      <div>
        <GoAFormItem label='No Fly Day?'>
          <div className='left'>
            {isEdit && (
              <GoARadioGroup
                name='rbtnNoFlyDay'
                value={flightReport?.noFlyDay === true ? 'Yes' : 'No'}
                orientation='horizontal'
                onChange={(_name, value) => {
                  let result = value === 'Yes' ? true : false;
                  //   onChange({ noFlyDay: result });
                  //   if (result) {
                  //     onNoFlyDay();
                  //   }
                  onNoFlyDay(result);
                }}
              >
                <GoARadioItem value='Yes' name={'Yes'} />
                <GoARadioItem value='No' name={'No'} />
              </GoARadioGroup>
            )}
            {!isEdit && <label>{flightReport?.noFlyDay === true ? 'Yes' : 'No'}</label>}
          </div>
        </GoAFormItem>
      </div>
      <div>
        <GoAFormItem label='Meter Start' error={flightReport?.meterStartError}>
          {isEdit && (
            <GoAInputText
              name='txtMeterStart'
              value={flightReport?.meterStart ? flightReport?.meterStart.toString() : ''}
              width='100%'
              maxLength={10}
              disabled={flightReport?.meterUS || flightReport?.noFlyDay}
              error={flightReport?.isMeterStartError}
              onChange={(name, value) => {
                if (value) {
                  if (validateDecimal(value, 1))
                    onChange({
                      meterStart: parseFloat(value),
                      isMeterStartError: false,
                      meterStartError: '',
                    });
                  else
                    onChange({
                      meterStart: parseFloat(value),
                      isMeterStartError: true,
                      meterStartError: FlightReportErrorMessages.MeterStartErrorMessage,
                    });
                } else {
                  onChange({ meterStart: undefined });
                }
              }}
            />
          )}
          {!isEdit && <label>{flightReport?.meterStart ? flightReport?.meterStart : '-'}</label>}
        </GoAFormItem>
      </div>
      <div>
        <GoAFormItem label='Meter Finish' error={flightReport?.meterFinishError}>
          {isEdit && (
            <GoAInputText
              name='txtMeterFinish'
              value={flightReport?.meterFinish ? flightReport?.meterFinish.toString() : ''}
              width='100%'
              maxLength={10}
              disabled={flightReport?.meterUS || flightReport?.noFlyDay}
              error={flightReport?.isMeterFinishError}
              onChange={(name, value) => {
                if (value) {
                  if (validateDecimal(value, 1))
                    onChange({
                      meterFinish: parseFloat(value),
                      isMeterFinishError: false,
                      meterFinishError: '',
                    });
                  else
                    onChange({
                      meterFinish: parseFloat(value),
                      isMeterFinishError: true,
                      meterFinishError: FlightReportErrorMessages.MeterFinishErrorMessage,
                    });
                } else {
                  onChange({ meterFinish: undefined });
                }
              }}
            />
          )}
          {!isEdit && <label>{flightReport?.meterFinish ? flightReport?.meterFinish : '-'}</label>}
        </GoAFormItem>
      </div>
      <div>
        <GoAFormItem label='Meter Difference'>
          {isEdit && (
            <GoAInputText
              name='txtMeterDifference'
              value={flightReport?.meterStart && flightReport?.meterFinish ? (flightReport?.meterFinish - flightReport?.meterStart).toFixed(1) : ''}
              width='100%'
              disabled={true}
              onChange={(name, value) => {}}
            />
          )}
          {!isEdit && (
            <label>
              {flightReport?.meterStart && flightReport?.meterFinish ? (flightReport?.meterFinish - flightReport?.meterStart).toFixed(1) : '-'}
            </label>
          )}
        </GoAFormItem>
      </div>
      <div>
        <GoAFormItem label='Ferry day'>
          <label>{flightReport.contractRegistration?.ferryDay ? 'Yes' : 'No'}</label>
        </GoAFormItem>
      </div>
      <div>
        <GoAFormItem error={flightReport.corporateRegionError} label='Forest Area' requirement={isEdit ? 'required' : undefined}>
          {isEdit && (
            <Select
              name='selForestArea'
              options={forestAreaOptions}
              placeholder='--Select--'
              className='width100'
              value={forestAreaOptions.find((t) => t.value === flightReport.corporateRegionId)}
              onChange={(value: any) => {
                if (value) {
                  const cr = getCorporateRegion(corporateRegionPaginationResult, value.value) ?? undefined;
                  onChange({
                    corporateRegion: cr,
                    corporateRegionId: value.value,

                    corporateRegionName: cr?.name ?? undefined,
                    corporateRegionError: cr ? '' : FlightReportErrorMessages.CorporateRegionErrorMessage,
                  });
                }
              }}
              isSearchable={true}
            />
          )}
          {!isEdit && <label>{flightReport?.corporateRegionName ? flightReport?.corporateRegionName : ''}</label>}
        </GoAFormItem>
      </div>
      <div></div>
      <div>
        <GoAFormItem label='Departure Location'>
          {isEdit && (
            <GoAInputText
              name='txtDepartureLocation'
              value={flightReport?.pointOfDeparture != null ? flightReport?.pointOfDeparture : ''}
              width='100%'
              maxLength={20}
              disabled={flightReport?.noFlyDay}
              onChange={(name, value) => {
                onChange({
                  pointOfDeparture: value.slice(0, 20),
                });
              }}
            />
          )}
          {!isEdit && <label>{flightReport?.pointOfDeparture ? flightReport?.pointOfDeparture : '-'}</label>}
        </GoAFormItem>
      </div>
      <div>
        {isEdit && (
          <>
            <GoAFormItem requirement='required' error={flightReport.downForNightLocationError} label='Down For Night Location'>
              <GoAInputText
                name='downForNightLocation'
                value={flightReport?.downForNightLocation ?? ''}
                width='100%'
                maxLength={20}
                onChange={(name, value) => {
                  if (value) {
                    onChange({
                      downForNightLocation: value.slice(0, 20),
                    });
                  } else {
                    onChange({
                      downForNightLocation: '',
                    });
                  }
                }}
              />
            </GoAFormItem>
          </>
        )}
        {!isEdit && (
          <GoAFormItem label='Down For Night Location'>
            <label>{flightReport?.downForNightLocation}</label>
          </GoAFormItem>
        )}
      </div>
      <div>
        <GoAFormItem label='Unserviceable Report?'>
          <div className='left'>
            {isEdit && (
              <GoARadioGroup
                name='rbtnUnserviceableReport'
                value={flightReport?.unserviceableReport === true ? 'Yes' : 'No'}
                orientation='horizontal'
                onChange={(_name, value) => {
                  let result = value === 'Yes' ? true : false;
                  onChangeUnservicableReport(result);
                  // onChange({
                  //     unserviceableReport: result,
                  //     isUnserviceableReportRefresh: result,
                  // });
                  // if (!result) {
                  //     onNotUnservicableReport();
                  // }
                }}
              >
                <GoARadioItem value='Yes' name={'Yes'} />
                <GoARadioItem value='No' name={'No'} />
              </GoARadioGroup>
            )}
            {!isEdit && <label>{flightReport?.unserviceableReport === true ? 'Yes' : 'No'}</label>}
          </div>
        </GoAFormItem>
      </div>
    </div>
  );
};
export default Summary;
